import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Button,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "reactstrap";

import { getHelpArticleUrl, resolveContextualHelp } from "../../helpers/helpCenter";
import {
  canAccessContextualHelp,
  canAccessOpenOrdersPanel,
} from "../../Routes/roleAccess";

const STEP_INDEXES = [1, 2, 3, 4, 5] as const;
const TIP_INDEXES = [1, 2, 3] as const;

const readOptionalList = (
  translate: (key: string, options?: { defaultValue?: string }) => string,
  baseKey: string,
  indexes: readonly number[]
): string[] =>
  indexes
    .map((index) => translate(`${baseKey}.${index}`, { defaultValue: "" }).trim())
    .filter((value) => value.length > 0);

const ContextualHelpOffcanvas = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const helpAllowed = canAccessContextualHelp();
  const ordersFabVisible = canAccessOpenOrdersPanel();

  const topic = useMemo(
    () => resolveContextualHelp(location.pathname),
    [location.pathname]
  );

  const moduleKey =
    topic.id === "fallback"
      ? "help.contextual.fallback"
      : `help.contextual.modules.${topic.id}`;

  const steps = useMemo(
    () => readOptionalList(t, `${moduleKey}.steps`, STEP_INDEXES),
    [moduleKey, t]
  );

  const tips = useMemo(
    () => readOptionalList(t, `${moduleKey}.tips`, TIP_INDEXES),
    [moduleKey, t]
  );

  const toggleOffcanvas = () => {
    setIsOpen((previous) => !previous);
  };

  if (!helpAllowed) {
    return null;
  }

  const fullHelpUrl = getHelpArticleUrl(topic.articlePath, i18n.language);
  // Empilhado acima do FAB de comandas; sozinho (ex.: professional) na posição base.
  const fabClassName = ordersFabVisible
    ? "contextual-help-setting d-none d-md-block"
    : "customizer-setting d-none d-md-block";

  return (
    <>
      <div className={fabClassName}>
        <Button
          color="soft-primary"
          className="btn-icon btn-lg p-2 rounded-pill shadow-lg"
          onClick={toggleOffcanvas}
          aria-label={t("help.contextual.fabLabel")}
        >
          <i className="ri-question-line fs-22" aria-hidden="true" />
        </Button>
      </div>

      <Offcanvas isOpen={isOpen} toggle={toggleOffcanvas} direction="end" className="border-0">
        <OffcanvasHeader toggle={toggleOffcanvas} className="border-bottom">
          {t(`${moduleKey}.title`)}
        </OffcanvasHeader>
        <OffcanvasBody>
          <p className="text-muted mb-3">{t(`${moduleKey}.summary`)}</p>

          {steps.length > 0 ? (
            <div className="mb-4">
              <h6 className="fs-14 mb-2">{t("help.contextual.stepsTitle")}</h6>
              <ol className="ps-3 mb-0">
                {steps.map((step, index) => (
                  <li key={`step-${index}`} className="mb-2">
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          {tips.length > 0 ? (
            <div className="mb-4">
              <h6 className="fs-14 mb-2">{t("help.contextual.tipsTitle")}</h6>
              <ul className="ps-3 mb-0">
                {tips.map((tip, index) => (
                  <li key={`tip-${index}`} className="mb-2 text-muted">
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <Button
            color="primary"
            className="w-100"
            tag="a"
            href={fullHelpUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("help.contextual.ctaFull")}
            <i className="ri-external-link-line ms-1 align-middle" aria-hidden="true" />
          </Button>
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default ContextualHelpOffcanvas;
